import * as React from "react";
import { useParams } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@src/ui/views/table";
import { Button } from "@src/ui/views/button";
import { Dialog, DialogContent, DialogTitle } from "@src/ui/views/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@src/ui/views/select";
import { Input } from "@src/ui/views/input";
import {
  useGetWelfareOccurrencePoliciesQuery,
  useCreateWelfareOccurrencePolicyMutation,
  useDeleteWelfareOccurrencePolicyMutation,
} from "@src/features/welfare/redux/welfare-api";
import type { WelfareOccurrencePolicy } from "@src/features/welfare/welfare-models";
import { X as XIcon } from "lucide-react";
import { MapPicker } from "@src/ui/compoments/map-picker";

// pick a default center (e.g. your org’s city) // CRAZY THAT JUST PUTTING THIS LINE OUTSIDE THE FUNCTION MAKES THE ENTIRE RERENDERING ON EVERY CLICK STOP!
// OR I COULD HAVE WRAPPEd IT IN USE MEMO
const DEFAULT_CENTER = { lat: 31.79461, lng: 35.19972 }

export function WelfareDefinitionPoliciesScreen() {
  const { spaceId, definitionId } = useParams<{
    spaceId: string;
    definitionId: string;
  }>();

  // Fetch existing policies
  const { data, isLoading, isError } = useGetWelfareOccurrencePoliciesQuery(
    { spaceId: spaceId!, definitionId: definitionId! },
    {
      // Tag by definition so deletes/creates will refetch
      providesTags: (_res, _err, { definitionId }) => [
        { type: "WelfareOccPolicies" as const, id: definitionId },
      ],
    }
  );

  // Mutations
  const [createPolicy, { isLoading: creating }] =
    useCreateWelfareOccurrencePolicyMutation();
  const [deletePolicy] = useDeleteWelfareOccurrencePolicyMutation();

  // Add-modal state
  const [addOpen, setAddOpen] = React.useState(false);
  const [dayOfWeek, setDayOfWeek] = React.useState(0);
  const [startHour, setStartHour] = React.useState(8);
  const [startMinute, setStartMinute] = React.useState(0);

  const [durationHours,   setDurationHours]   = React.useState<number>(0);
  const [durationMinutes, setDurationMinutes] = React.useState<number>(0);
  const [locationName,    setLocationName]    = React.useState<string>("");
  const [position, setPosition]           = React.useState<{ lat: number; lng: number } | null>(null);
  const handleMapClick = React.useCallback((pos: {lat:number; lng:number}) => {
    setPosition(pos);
    setLocationName("");    // clear any manual name
  }, []);
  


  if (isLoading) return <p>Loading policies…</p>;
  if (isError || !data) return <p>Error loading policies</p>;

  const policies: WelfareOccurrencePolicy[] = data.data;
  const dayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  return (
    <div className="p-4">
      {/* Header with "Add Policy" */}
      <div className="flex items-center justify-between mb-4">
        <Button onClick={() => setAddOpen(true)}>Add Policy</Button>
      </div>

      {/* Policies Table */}
      <Table className="w-full mb-6">
        <TableHeader>
          <TableRow>
            <TableHead>Day</TableHead>
            <TableHead>Time</TableHead>
            <TableHead>Duration</TableHead>
            <TableHead>Location Name</TableHead>
            <TableHead className="w-12" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {policies.map((pol) => (
            <TableRow
              key={pol.policyId}
              className="group hover:bg-gray-50"
            >
              <TableCell>{dayNames[pol.dayOfWeek]}</TableCell>
              <TableCell>
                {String(pol.startHour).padStart(2, "0")}:
                {String(pol.startMinute).padStart(2, "0")}
              </TableCell>
              <TableCell>
                {String(pol.durationHours).padStart(2, "0")}:
                {String(pol.durationMinutes).padStart(2, "0")}
              </TableCell>
              <TableCell>
                {String(pol.locationName)}
              </TableCell>
              <TableCell className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() =>
                    deletePolicy({
                      spaceId: spaceId!,
                      definitionId: definitionId!,
                      policyId: pol.policyId,
                    })
                  }
                >
                  <XIcon className="h-4 w-4 text-red-600" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Add Policy Modal */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm">
          <DialogTitle>Add Occurrence Policy</DialogTitle>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium">Day of Week</label>
              <Select
                onValueChange={(v) => setDayOfWeek(Number(v))}
                value={String(dayOfWeek)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select day…" />
                </SelectTrigger>
                <SelectContent>
                  {dayNames.map((name, i) => (
                    <SelectItem key={i} value={String(i)}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex space-x-2">
              <div className="flex-1">
                <label className="block text-sm font-medium">Hour</label>
                <Input
                  type="number"
                  min={0}
                  max={23}
                  value={startHour}
                  onChange={(e) => setStartHour(Number(e.target.value))}
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium">Minute</label>
                <Input
                  type="number"
                  min={0}
                  max={59}
                  value={startMinute}
                  onChange={(e) => setStartMinute(Number(e.target.value))}
                />
              </div>
            </div>

                    <div className="flex space-x-2">
            <div className="flex-1">
              <label className="block text-sm font-medium">Duration Hours</label>
              <Input
                type="number"
                min={0}
                value={durationHours}
                onChange={e => setDurationHours(Number(e.target.value))}
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium">Duration Minutes</label>
              <Input
                type="number"
                min={0}
                value={durationMinutes}
                onChange={e => setDurationMinutes(Number(e.target.value))}
              />
            </div>
          </div>

     {/* Map picker */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Pick Location on Map
              </label>
              <MapPicker
                defaultCenter={DEFAULT_CENTER}
                onChange={handleMapClick}
              />
              {position && (
                <p className="mt-2 text-sm text-gray-700">
                  Selected: {position.lat.toFixed(5)}, {position.lng.toFixed(5)}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Click on the map to choose lat/lng.
              </p>
            <div className="mt-2">
              <label className="block text-sm font-medium">Location Name</label>
              <Input
                type="text"
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
              />
            </div>
          </div>

            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setAddOpen(false)}>
                Cancel
              </Button>
              <Button
                disabled={creating}
                onClick={async () => {
                  await createPolicy({
                    spaceId: spaceId!,
                    definitionId: definitionId!,
                    dayOfWeek,
                    startHour,
                    startMinute,
                    durationHours, 
                    durationMinutes, 
                    locationName, 
                    locationLat: position?.lat,
                    locationLng: position?.lng,
                  }).unwrap();
                  setAddOpen(false);
                }}
              >
                {creating ? "Adding…" : "Add"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
